import { useMemo } from 'react';
import { DemeterMarket, DemeterPageElementModel } from '../../../../Generated/Raven-Demeter';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import { ITreeNode } from '../../TreeView/TreeItem';
import TreeView from '../../TreeView/TreeView';
import { IPageElementModalSelection } from './PageElementModalDefinitions';

interface IPageElementMarketAndCommodityProps {
    selection: IPageElementModalSelection;
    pageElements: DemeterPageElementModel[];
    handleMarketSelect: (selected: DemeterMarket | null | undefined) => void;
    handleCommoditySelect: (selectedName: string | null | undefined) => void;
    track: TrackingEvent;
}

const PageElementMarketAndCommodity: React.FC<IPageElementMarketAndCommodityProps> = (props) => {
    const [translations, translate] = useLanguage();

    const equals = (a: any, b: any) => {
        if (a === b) {
            return true;
        }
        if ((a === null || a === undefined) && (b === null || b === undefined)) {
            return true;
        }
        return String(a) === String(b);
    };

    const findNodeById = (nodes: ITreeNode[], id: string): ITreeNode | undefined =>
        nodes.reduce<ITreeNode | undefined>((found, node) => {
            if (found) {
                return found;
            }
            if (node.id === id) {
                return node;
            }
            return findNodeById(node.children, id);
        }, undefined);

    const commodityTreeNodes: ITreeNode[] = useMemo(() => {
        const map = new Map<string, ITreeNode>();

        props.pageElements.forEach((pageElement) => {
            const { market, commodity, commodityDisplayName } = pageElement;

            const marketName = translations.market[market as DemeterMarket] || market || '';
            const marketId = `${market}`;
            const marketValue = `${marketId}`;

            const commodityName = commodityDisplayName ? translate(commodityDisplayName) || commodityDisplayName : commodity ?? '';
            const commodityId = `${marketId}|${commodityName}`;
            const commodityValue = `${commodityId}`;

            const isSelectedMarket = equals(marketValue, props.selection.market);

            // Market node
            if (!map.has(marketId)) {
                map.set(marketId, {
                    id: marketId,
                    name: marketName,
                    value: marketValue,
                    parentId: '',
                    parentValue: '',
                    column: 'left',
                    selectedProduct: isSelectedMarket,
                    expanded: isSelectedMarket,
                    order: 0,
                    displayOrder: 0,
                    children: [],
                });
            }

            const marketNode = map.get(marketId)!;
            const isSelectedCommodity = isSelectedMarket && equals(commodityName, props.selection.commodityName);

            // Commodity node
            if (commodity && !marketNode.children.find((n) => n.id === commodityId)) {
                marketNode.children.push({
                    id: commodityId,
                    name: commodityName,
                    value: commodityValue,
                    parentId: marketId,
                    parentValue: commodityValue,
                    column: 'left',
                    selectedProduct: isSelectedCommodity,
                    expanded: false,
                    order: 0,
                    displayOrder: 0,
                    children: [],
                });
            }
        });

        return Array.from(map.values());
    }, [props.pageElements, props.selection.market, props.selection.commodityName]);

    const treeNodes: ITreeNode[] = useMemo(() => commodityTreeNodes, [commodityTreeNodes]);

    const handleSelect = (event: React.MouseEvent<HTMLElement>) => {
        const { id } = event.currentTarget;
        const node = findNodeById(treeNodes, id);
        if (!node) {
            return;
        }

        const [market, commodityName] = node.value.split('|');

        if (market === 'null') {
            props.handleMarketSelect(undefined);
        } else {
            props.handleMarketSelect(market as DemeterMarket);
        }
        if (commodityName === 'null') {
            props.handleCommoditySelect(undefined);
        } else {
            props.handleCommoditySelect(commodityName);
        }
    };

    return <TreeView data={treeNodes} searchTerm="" handleExpand={handleSelect} handleSelect={handleSelect} disabledNodes={[]} track={props.track} />;
};

export default PageElementMarketAndCommodity;
